// Inbound Routing — manage tracking numbers and their hunt-group pools.
//
// Workflow:
//   1. Buy a Twilio number (or use one you already own) and add it here as a "Tracking Number"
//   2. Add 1-N pool entries (cell phones, agent DIDs, fallback lines)
//   3. Configure routing mode (round-robin default), per-leg timeout, fallback
//   4. Drop the published number into your ad creative — calls hunt-group through the pool
//
// CIVIX auto-creates a Lead on first inbound + logs the Call record so you have full CRM trail.

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, apiDel as apiDelete } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { Phone, Plus, Trash2, ToggleLeft, ToggleRight, ChevronDown, ChevronRight, Users, Zap, Clock, BarChart3, ArrowUpDown } from 'lucide-react';
import { fmtPhone } from '../lib/format.js';
import toast from 'react-hot-toast';

export default function InboundRouting() {
  const { data, refetch } = useQuery({
    queryKey: ['inbound-routing'],
    queryFn: () => apiGet('/inbound-routing'),
  });
  const items = data?.items || [];
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="space-y-4">
      <TopBar title="Inbound Call Routing" subtitle="Tracking numbers + hunt-group forwarding" />

      <div className="panel p-5">
        <div className="flex items-start justify-between mb-1">
          <div>
            <div className="font-display font-bold text-lg flex items-center gap-2">
              <Phone className="w-4 h-4" /> Tracking Numbers
            </div>
            <div className="text-xs text-white/50 mt-1">
              Calls to these numbers fan out across your forward-to pool. First to answer wins.
            </div>
          </div>
          <button
            className="btn-primary text-xs flex items-center gap-1.5"
            onClick={() => setShowCreate(true)}
          >
            <Plus className="w-3.5 h-3.5" /> Add Tracking Number
          </button>
        </div>
      </div>

      {items.length === 0 && (
        <div className="panel p-10 text-center text-white/50 text-sm">
          No tracking numbers yet. Add one to start routing inbound calls through a hunt group.
        </div>
      )}

      {items.map((n) => (
        <RoutingNumberCard
          key={n._id}
          item={n}
          expanded={expandedId === n._id}
          onToggle={() => setExpandedId(expandedId === n._id ? null : n._id)}
          onChange={refetch}
        />
      ))}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={refetch} />}
    </div>
  );
}

function RoutingNumberCard({ item, expanded, onToggle, onChange }) {
  const [newPhone, setNewPhone] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  const stats = item.stats || {};
  const activePool = (item.pool || []).filter((p) => p.active);

  async function addPoolEntry(e) {
    e.preventDefault();
    if (!newPhone.trim()) return toast.error('Phone required');
    try {
      await apiPost(`/inbound-routing/${item._id}/pool`, {
        phone: newPhone.trim(),
        label: newLabel.trim(),
        active: true,
      });
      toast.success('Forward-to added');
      setNewPhone('');
      setNewLabel('');
      onChange?.();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  async function removePoolEntry(entryId) {
    if (!confirm('Remove this forward-to entry?')) return;
    try {
      await apiDelete(`/inbound-routing/${item._id}/pool/${entryId}`);
      toast.success('Removed');
      onChange?.();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  async function togglePoolEntry(entry) {
    try {
      const newPool = (item.pool || []).map((p) =>
        String(p._id) === String(entry._id) ? { ...p, active: !p.active } : p
      );
      await apiPut(`/inbound-routing/${item._id}`, { pool: newPool });
      onChange?.();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  async function updateMeta(field, value) {
    setSavingMeta(true);
    try {
      await apiPut(`/inbound-routing/${item._id}`, { [field]: value });
      onChange?.();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setSavingMeta(false);
    }
  }

  async function deleteNumber() {
    if (!confirm(`Delete ${item.name}? This will stop all calls to ${fmtPhone(item.twilioNumber)}.`)) return;
    try {
      await apiDelete(`/inbound-routing/${item._id}`);
      toast.success('Tracking number deleted');
      onChange?.();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  }

  return (
    <div className="panel">
      <button
        className="w-full p-5 flex items-center justify-between hover:bg-civix-panelLight transition rounded-t-lg"
        onClick={onToggle}
      >
        <div className="flex items-center gap-4 text-left">
          {expanded ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
          <div>
            <div className="flex items-center gap-2">
              <span className="font-display font-bold text-base">{item.name}</span>
              {!item.active && <span className="pill-cold text-[10px]">PAUSED</span>}
            </div>
            <div className="text-sm text-white/50 mt-0.5">{fmtPhone(item.twilioNumber)} · {activePool.length} active forward-to</div>
          </div>
        </div>
        <div className="flex items-center gap-6 text-xs text-white/60">
          <div className="flex items-center gap-1.5"><BarChart3 className="w-3.5 h-3.5" />{stats.totalCalls || 0} calls</div>
          <div className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" />{stats.totalLeadsCreated || 0} leads</div>
          <div className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" />{stats.totalAnswered || 0} answered</div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-civix-border p-5 space-y-5">
          {/* Settings row */}
          <div className="grid md:grid-cols-4 gap-4 text-xs">
            <div>
              <div className="text-white/50 mb-1">Routing Mode</div>
              <select className="input" value={item.routingMode} onChange={(e) => updateMeta('routingMode', e.target.value)} disabled={savingMeta}>
                <option value="round_robin">Round-robin (next in line)</option>
                <option value="first_in_pool">Always ring first entry first</option>
                <option value="random">Random pick</option>
              </select>
            </div>
            <div>
              <div className="text-white/50 mb-1">Ring duration per leg (sec)</div>
              <input
                type="number"
                className="input"
                defaultValue={item.legTimeoutSec}
                onBlur={(e) => updateMeta('legTimeoutSec', parseInt(e.target.value, 10) || 18)}
                min={5}
                max={60}
              />
            </div>
            <div>
              <div className="text-white/50 mb-1">If nobody answers</div>
              <select className="input" value={item.fallbackMode} onChange={(e) => updateMeta('fallbackMode', e.target.value)} disabled={savingMeta}>
                <option value="voicemail">Take voicemail</option>
                <option value="hangup">Hang up with message</option>
              </select>
            </div>
            <div>
              <div className="text-white/50 mb-1">Status</div>
              <button
                onClick={() => updateMeta('active', !item.active)}
                className={`px-3 py-2 rounded-md text-xs flex items-center gap-2 w-full justify-center ${item.active ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/40' : 'bg-rose-500/15 text-rose-300 border border-rose-500/40'}`}
              >
                {item.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                {item.active ? 'Active' : 'Paused'}
              </button>
            </div>
          </div>

          {/* Pool list */}
          <div>
            <div className="text-xs text-white/60 mb-2 flex items-center gap-1.5"><ArrowUpDown className="w-3.5 h-3.5" />Forward-to Pool ({(item.pool || []).length})</div>
            {(item.pool || []).length === 0 && (
              <div className="text-xs text-white/40 italic mb-2">No forward-to numbers yet. Add one below.</div>
            )}
            <div className="space-y-2">
              {(item.pool || []).map((p, idx) => (
                <div key={p._id} className="flex items-center gap-3 p-3 rounded-lg bg-civix-panelLight/40 border border-civix-border">
                  <div className="text-xs text-white/40 w-6">#{idx + 1}</div>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{fmtPhone(p.phone) || '—'}</div>
                    {p.label && <div className="text-xs text-white/50">{p.label}</div>}
                  </div>
                  <div className="text-xs text-white/50 flex items-center gap-3">
                    <span title="Total times this entry was rung">{p.totalRings || 0} rings</span>
                    <span title="Times this entry answered">{p.totalAnswered || 0} answered</span>
                  </div>
                  <button
                    onClick={() => togglePoolEntry(p)}
                    className={`p-1.5 rounded-sm ${p.active ? 'text-emerald-300' : 'text-white/30'}`}
                    title={p.active ? 'Pause' : 'Activate'}
                  >
                    {p.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => removePoolEntry(p._id)}
                    className="p-1.5 rounded-sm text-white/40 hover:text-rose-300"
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <form onSubmit={addPoolEntry} className="mt-3 flex gap-2">
              <input
                className="input flex-1"
                placeholder="Phone (e.g. +13035551234)"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
              />
              <input
                className="input flex-1"
                placeholder="Label (e.g. Easton's cell)"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
              />
              <button type="submit" className="btn-primary text-xs flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" />Add</button>
            </form>
          </div>

          {/* Twilio webhook config helper */}
          <div className="text-xs text-white/40 border-t border-civix-border pt-4">
            <div className="font-semibold text-white/60 mb-1">Twilio Setup (for {fmtPhone(item.twilioNumber)})</div>
            <div>In Twilio Console → Phone Numbers → {fmtPhone(item.twilioNumber)} → Voice Configuration:</div>
            <div className="mt-1 font-mono text-[11px] bg-black/30 px-2 py-1.5 rounded-sm">
              A CALL COMES IN: <span className="text-emerald-300">Webhook</span> → <span className="text-amber-300">https://api.civixcrm.ai/api/inbound-routing/voice</span> · HTTP POST
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-civix-border">
            <button onClick={deleteNumber} className="text-xs text-rose-300/70 hover:text-rose-300 flex items-center gap-1.5">
              <Trash2 className="w-3.5 h-3.5" /> Delete Tracking Number
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const SOURCE_PRESETS = [
  { value: 'google_ads_call', label: 'Google Ads',     tags: ['inbound-call', 'google-ads'] },
  { value: 'meta_ads_call',   label: 'Meta Ads (FB/IG)', tags: ['inbound-call', 'meta-ads'] },
  { value: 'tiktok_ads_call', label: 'TikTok Ads',     tags: ['inbound-call', 'tiktok-ads'] },
  { value: 'organic_call',    label: 'Organic / SEO',  tags: ['inbound-call', 'organic'] },
  { value: 'billboard_call',  label: 'Billboard / OOH', tags: ['inbound-call', 'billboard'] },
  { value: 'radio_call',      label: 'Radio',          tags: ['inbound-call', 'radio'] },
  { value: 'referral_call',   label: 'Referral',       tags: ['inbound-call', 'referral'] },
  { value: 'inbound_call',    label: 'Generic / Other', tags: ['inbound-call'] },
];

function CreateModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [twilioNumber, setTwilioNumber] = useState('');
  const [productInterest, setProductInterest] = useState('');
  const [sourcePreset, setSourcePreset] = useState('inbound_call');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!name.trim() || !twilioNumber.trim()) return toast.error('Name + number required');
    setBusy(true);
    const preset = SOURCE_PRESETS.find((p) => p.value === sourcePreset) || SOURCE_PRESETS[SOURCE_PRESETS.length - 1];
    try {
      await apiPost('/inbound-routing', {
        name: name.trim(),
        twilioNumber: twilioNumber.trim(),
        active: true,
        autoCreateLead: true,
        leadDefaults: { tags: preset.tags, productInterest: productInterest.trim(), source: preset.value },
        pool: [],
      });
      toast.success('Tracking number added');
      onCreated?.();
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-xs flex items-center justify-center z-50 p-4">
      <div className="panel max-w-md w-full p-6">
        <div className="font-display font-bold text-lg mb-1">Add Tracking Number</div>
        <div className="text-xs text-white/50 mb-4">Use a Twilio number you already own. After saving, point its voice webhook at the URL shown on the card.</div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <div className="text-xs text-white/60 mb-1">Name</div>
            <input className="input w-full" placeholder="Google Ads — Life Tracking" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1">Twilio number (E.164)</div>
            <input className="input w-full" placeholder="+18005551234" value={twilioNumber} onChange={(e) => setTwilioNumber(e.target.value)} />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1">Ad channel</div>
            <select className="input w-full" value={sourcePreset} onChange={(e) => setSourcePreset(e.target.value)}>
              {SOURCE_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <div className="text-[11px] text-white/40 mt-1">Auto-tags every Lead created from this number so you can filter/report by channel.</div>
          </div>
          <div>
            <div className="text-xs text-white/60 mb-1">Product interest tag (optional)</div>
            <input className="input w-full" placeholder="Term Life" value={productInterest} onChange={(e) => setProductInterest(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50">{busy ? 'Saving…' : 'Add Number'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
